<template>
  <div ref="root" :class="['AlertStack', `AlertStack__${position}`]">
    <TransitionGroup name="AlertStack__item">
      <UIAlert
        v-for="alert in alertStore.alerts"
        :key="alert.id"
        class="AlertStack__item"
        :role="alert.type === 'error' ? 'alert' : 'status'"
        :theme="FEEDBACK_THEME_MAP[alert.type].theme"
        :icon="FEEDBACK_THEME_MAP[alert.type].icon"
        :title="alert.title"
        titleTag="h5"
        :description="toPlainText(alert.message)"
        @dismiss="alertStore.removeAlert(alert.id)"
      >
        <template v-if="alert.actions" #actions>
          <UIButton
            v-for="([label, action], index) in Object.entries(alert.actions)"
            :key="`${label}_${index}`"
            theme="tertiary"
            size="sm"
            @click="action()"
          >
            {{ label }}
          </UIButton>
        </template>
      </UIAlert>
    </TransitionGroup>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUpdated, ref, watch } from 'vue'

import { UIAlert, UIButton } from '@foundever/ui-lib'

import { useAlert } from '@/store/alert'

import { ALERT_POSITION } from './types'
import {
  FEEDBACK_THEME_MAP,
  labelDismissButtons,
  toPlainText,
} from '../toasts/types'

const alertStore = useAlert()

const root = ref<HTMLElement | null>(null)
onMounted(() => labelDismissButtons(root.value))
onUpdated(() => labelDismissButtons(root.value))
// Items render inside TransitionGroup's slot, so the stack itself does not
// re-render when they change; relabel after the DOM update as well.
watch(
  () => alertStore.alerts.map((a) => a.id).join(','),
  () => labelDismissButtons(root.value),
  { flush: 'post' },
)

withDefaults(
  defineProps<{
    position?: ALERT_POSITION
  }>(),
  {
    position: ALERT_POSITION.BOTTOM_RIGHT,
  },
)
</script>

<style lang="scss" scoped>
.AlertStack {
  z-index: 1998;
  position: fixed;
  padding: 1rem;
  max-width: 27rem;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  pointer-events: none;

  &__item {
    pointer-events: auto;
    box-sizing: border-box;

    :deep(.UIAlert__container) {
      min-width: 0;
    }
    :deep(.UIAlert__title),
    :deep(.UIAlert__description) {
      overflow-wrap: anywhere;
    }

    // AlertStack item animation
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(1.5rem);
    }
  }

  &__top-right {
    top: 0;
    right: 0;
    align-items: flex-end;
  }

  &__top-left {
    top: 0;
    left: 0;
    align-items: flex-start;
  }

  &__top-center {
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    align-items: center;
  }

  &__bottom-left {
    bottom: 0;
    left: 0;
    align-items: flex-start;
  }

  &__bottom-right {
    bottom: 0;
    right: 0;
    align-items: flex-end;
  }
}
</style>
